import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CircularProgress } from '@mui/material';
import { useProject } from '../contexts/ProjectContext';
import FontManagement, { FontInfo } from '../components/FontManagement';
import LogoManagement, { LogoInfo } from '../components/LogoManagement';
import { VideoSettingsPanel, VideoSettings } from '../components/VideoSettings';
import { DefaultCardSettingsPanel } from '../components/DefaultCardSettingsPanel';
import CardFramingPanel from '../components/CardFramingPanel';
import { CollapsibleSection } from '../components/CollapsibleSection';
import GoogleSheetsLoader from '../loaders/GoogleSheetsLoader';
import type { LoaderOnFileUpload } from '../loaders/shared';
import NavBar from '../components/NavBar';
import type { Block } from '../classes/Block';
import type { CardGroup } from '../utils/cardGroups';
import type { DefaultCardSettings } from '../utils/defaultCardSettings';
import type { FontMetadata } from '../utils/fontHelpers';
import './ProjectSettings.css';

interface ProjectSettingsHydrationProps {
  onFileUpload: LoaderOnFileUpload;
  hasExistingData: boolean;
  onSheetsUrlUpdate?: (url: string | null) => void;
  initialTemplateSpreadsheetId?: string;
  onInitialTemplateConsumed?: () => void;
  loadButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

interface ProjectSettingsProps {
  fonts: FontInfo[];
  logos: LogoInfo[];
  videoSettings: VideoSettings;
  defaultCardSettings: DefaultCardSettings;
  blocks: Block[];
  cardGroups: CardGroup[];
  fps: number;
  onFontListChange: (fonts: FontInfo[]) => void;
  onLogoListChange: (logos: LogoInfo[]) => void;
  onVideoSettingsChange: (settings: VideoSettings) => void;
  onDefaultCardSettingsChange: (
    newDefaults: DefaultCardSettings,
    changedField: keyof DefaultCardSettings,
    overrideCardGroupIds: string[]
  ) => void;
  onFontUploaded?: (fontName: string, fontMetadata: FontMetadata) => void;
  onLogoUploaded?: (logoName: string, serverUrl: string) => void;
  onFontDeleted?: (fontName: string) => void;
  onLogoDeleted?: (logoName: string) => void;
  hydration?: ProjectSettingsHydrationProps;
}

const ProjectSettings: React.FC<ProjectSettingsProps> = ({
  fonts,
  logos,
  videoSettings,
  defaultCardSettings,
  blocks,
  cardGroups,
  fps,
  onFontListChange,
  onLogoListChange,
  onVideoSettingsChange,
  onDefaultCardSettingsChange,
  onFontUploaded,
  onLogoUploaded,
  onFontDeleted,
  onLogoDeleted,
  hydration,
}) => {
  const { selectedProject } = useProject();
  const navigate = useNavigate();
  const [hydrationState, setHydrationState] = useState<'loading' | 'ready'>(
    hydration && !hydration.hasExistingData ? 'loading' : 'ready'
  );

  useEffect(() => {
    setHydrationState(hydration && !hydration.hasExistingData ? 'loading' : 'ready');
  }, [hydration, hydration?.hasExistingData, selectedProject?.id]);

  if (!selectedProject) {
    return null;
  }

  if (hydrationState === 'loading' && hydration && !hydration.hasExistingData) {
    return (
      <div className="project-settings-page">
        <div style={{ display: 'none' }}>
          <GoogleSheetsLoader
            onFileUpload={hydration.onFileUpload}
            hasExistingData={hydration.hasExistingData}
            onSheetsUrlUpdate={hydration.onSheetsUrlUpdate}
            initialTemplateSpreadsheetId={hydration.initialTemplateSpreadsheetId}
            onInitialTemplateConsumed={hydration.onInitialTemplateConsumed}
            onAutoLoadStateChange={setHydrationState}
            loadButtonRef={hydration.loadButtonRef}
          />
        </div>
        <NavBar />
        <div
          style={{
            minHeight: 'calc(100vh - 60px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className="project-settings-page">
      {hydration ? (
        <div style={{ display: 'none' }}>
          <GoogleSheetsLoader
            onFileUpload={hydration.onFileUpload}
            hasExistingData={hydration.hasExistingData}
            onSheetsUrlUpdate={hydration.onSheetsUrlUpdate}
            initialTemplateSpreadsheetId={hydration.initialTemplateSpreadsheetId}
            onInitialTemplateConsumed={hydration.onInitialTemplateConsumed}
            onAutoLoadStateChange={setHydrationState}
            loadButtonRef={hydration.loadButtonRef}
          />
        </div>
      ) : null}
      <NavBar />
      <div className="project-settings-content">
        <button
          onClick={() => navigate('/app')}
          className="fancy-button"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}
        >
          <ArrowBackIcon style={{ fontSize: '18px' }} />
          {t`Back to Editor`}
        </button>

        <div className="settings-sections">
          <div className="video-settings-section">
            <CollapsibleSection title={String(t`Video Settings`)} contentClassName="video-settings-section-body">
              <VideoSettingsPanel
                settings={videoSettings}
                onSettingsChange={onVideoSettingsChange}
                showTitle={false}
              />
            </CollapsibleSection>
          </div>
          {videoSettings.mode === 'cards' && (
            <div className="video-settings-section">
              <CollapsibleSection title={String(t`Default Card Settings`)} contentClassName="video-settings-section-body">
                <DefaultCardSettingsPanel
                  defaultCardSettings={defaultCardSettings}
                  onDefaultsChange={onDefaultCardSettingsChange}
                  blocks={blocks}
                  cardGroups={cardGroups}
                  fps={fps}
                />
              </CollapsibleSection>
            </div>
          )}
          {videoSettings.mode === 'cards' && (
            <div className="video-settings-section">
              <CollapsibleSection title={String(t`Card Framing`)} contentClassName="video-settings-section-body">
                <CardFramingPanel
                  settings={videoSettings}
                  onSettingsChange={onVideoSettingsChange}
                />
              </CollapsibleSection>
            </div>
          )}
          <div style={{rowGap: '14px', display: 'flex', flexDirection: 'column'}}>
          <FontManagement
            onFontListChange={onFontListChange}
            initialFonts={fonts}
            onFontUploaded={onFontUploaded}
            onFontDeleted={onFontDeleted}
          />

          <LogoManagement
            onLogoListChange={onLogoListChange}
            initialLogos={logos}
            onLogoUploaded={onLogoUploaded}
            onLogoDeleted={onLogoDeleted}
          />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSettings;
